<script setup lang="ts">
import { useRoute } from 'vue-router'
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'

import UiHeader from '@/components/UiHeader/UiHeader.vue'
import UiHero from '@/components/UiHero/UiHero.vue'
import UiAntForm from '@/components/UiForm/UiAntForm.vue'
import UiFooter from '@/components/UiFooter/UiFooter.vue'
import PartnerTermCard from '@/components/PartnerTermCard/PartnerTermCard.vue'
import type { PartnerOfferTerm } from '@/types'

interface LegalEntity {
  name: string | null
  inn: string | null
  ogrn: string | null
  offer: string | null
}

interface PartnerLink {
  id: string
  provider: string
  product_name: string
  price: number
  legal_entity: LegalEntity | null
  show_accept_all_terms?: boolean
}

const route = useRoute()
const unavailable = ref<boolean>(false)
const partnerLink = ref<PartnerLink | null>(null)
const pageLoader = ref(true)

async function getPartnerLink(uuid: string | string[]) {
  try {
    const response = await axios.get<PartnerLink>(`/l/${uuid}`)
    partnerLink.value = response.data
  } catch (error) {
    unavailable.value = true
  }
}

const offerUrls = computed<string[]>(() => {
  const offer = partnerLink.value?.legal_entity?.offer
  return offer ? [offer] : []
})

const submitUrl = computed(() => `/l/${route.params.uuid}/submit`)

// Несколько сроков в офере: после анкеты — страница выбора срока.
const terms = ref<PartnerOfferTerm[]>([])
const requestId = ref<string | null>(null)
const showTerms = computed(() => terms.value.length > 0)

function onSubmitSuccess(data: { redirect_url?: string; request_id?: string; terms?: PartnerOfferTerm[] }) {
  if (data.redirect_url) {
    window.location.href = data.redirect_url
    return
  }

  if (data.terms?.length) {
    requestId.value = data.request_id ?? null
    terms.value = [...data.terms].sort((a, b) => a.sort_order - b.sort_order || a.term_months - b.term_months)
  }
}

async function chooseTerm(term: PartnerOfferTerm) {
  try {
    const response = await axios.post<{ redirect_url: string }>('/l/choose-term', {
      request_id: requestId.value,
      term_id: term.id
    })
    window.location.href = response.data.redirect_url
  } catch (error) {
    unavailable.value = true
  }
}

onMounted(async () => {
  try {
    await getPartnerLink(route.params.uuid)
  } finally {
    pageLoader.value = false
  }
})
</script>

<template>
  <div v-if="pageLoader || partnerLink" class="payment-cabinet">
    <div>
      <ui-header :logo-loading="pageLoader" />

      <template v-if="partnerLink">
        <ui-hero
          :title="partnerLink.product_name"
          :subtitle="`${partnerLink.price} ₽`"
        />

        <template v-if="showTerms">
          <div class="partner-link-terms container">
            <h2 class="partner-link-terms__title">Выберите комфортный срок и платёж для вашей рассрочки</h2>
            <p class="partner-link-terms__subtitle">После выбора срока вы перейдёте к заполнению основной анкеты. Обычно это занимает не более 3-5 минут.</p>

            <PartnerTermCard
              v-for="term in terms"
              :key="term.id"
              :term-months="term.term_months"
              :monthly-payment="term.monthly_payment"
              @select="chooseTerm(term)"
            />
          </div>
        </template>

        <ui-ant-form
          v-else
          :form-options="null"
          :offer-urls="offerUrls"
          :show-accept-all="partnerLink.show_accept_all_terms"
          :submit-url="submitUrl"
          redirect-field="redirect_url"
          :on-submit-success="onSubmitSuccess"
        />

        <ui-footer />
      </template>
    </div>
  </div>

  <div v-if="unavailable" class="payment-cabinet">
    <div class="container">
      <ui-hero title="Страница недоступна" />
    </div>
  </div>
</template>

<style lang="scss">
.partner-link-terms {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__title {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
  }

  &__subtitle {
    margin: 0 0 8px;
    font-size: 14px;
    color: #6b7280;
  }
}
</style>
